import type { StateAction } from "@entrilia/shared/types";
import { sumBy } from "@entrilia/shared/utilities/arrayHelper";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import type { DataImportFileContainerInfo } from "../../../../../api/types/dataImportTypes";

export interface FileContainerEditState {
  name: string;
  files: File[];
  isSaving: boolean;
  addFilesError?: string;
  uploadedFileName?: string;
  totalProgress?: number; // [0; 1]
}

export const getInitialFileContainerEditState = (
  editedContainer?: DataImportFileContainerInfo
): FileContainerEditState => ({
  name: editedContainer?.containerName ?? "",
  files: [],
  isSaving: false,
});

export const acceptedFileExtensions = [
  ".zip",
  ".pdf",
  ".mp4",
  ".webm",
  ".ogv",
  ".mov",
  ".docx",
  ".doc",
  ".xlsx",
  ".xlsm",
  ".xls",
  ".pptx",
  ".ppt",
];

export const isStateValid = (state: FileContainerEditState): boolean => {
  return !!state.name.trim() && state.files.length > 0;
};

// Actions

export const changeNameAction =
  (name: string): StateAction<FileContainerEditState> =>
  (state) => ({
    ...state,
    name,
  });

export const addFilesAction =
  (files: File[]): StateAction<FileContainerEditState> =>
  (state) => {
    const validFiles: File[] = [];
    const invalidFileExtensions = new Set<string>();
    const existingNames = new Set<string>();

    files.forEach((file) => {
      const fileExtension = getFileExtension(file.name);

      if (!acceptedFileExtensions.includes(fileExtension)) {
        invalidFileExtensions.add(fileExtension);
      } else if (state.files.some((f) => f.name === file.name)) {
        existingNames.add(file.name);
      } else {
        validFiles.push(file);
      }
    });

    let errorMessage =
      invalidFileExtensions.size > 0
        ? `The following extensions are not supported: ${Array.from(invalidFileExtensions).join(", ")}. `
        : "";

    if (existingNames.size > 0) {
      errorMessage += `Files with the following names were already added: ${Array.from(existingNames).join(", ")}.`;
    }

    return {
      ...state,
      files: [...state.files, ...validFiles],
      addFilesError: errorMessage || undefined,
    };
  };

export const removeFileAction =
  (fileName: string): StateAction<FileContainerEditState> =>
  (state) => ({
    ...state,
    files: state.files.filter((file) => file.name !== fileName),
  });

export const startSavingAction = (): StateAction<FileContainerEditState> => (state) => ({
  ...state,
  isSaving: true,
  addFilesError: undefined,
});

export const finishSavingAction =
  (totalProgress: number): StateAction<FileContainerEditState> =>
  (state) => ({
    ...state,
    isSaving: false,
    totalProgress,
    uploadedFileName: undefined,
  });

// uploadedFileProgress is [0; 1]
export const updateUploadedFileProgressAction =
  (
    uploadedFileName: string,
    uploadedFileIndex: number,
    uploadedFileProgress: number
  ): StateAction<FileContainerEditState> =>
  (state) => {
    const filesTotalSize = sumBy(state.files, (file) => file.size);

    const uploadedFilesTotalSize = state.files.reduce((result, file, index) => {
      const currentFileSize =
        index === uploadedFileIndex ? uploadedFileProgress * file.size : index < uploadedFileIndex ? file.size : 0;
      return result + currentFileSize;
    }, 0);

    const totalProgress = Math.max(0, Math.min(1, uploadedFilesTotalSize / (filesTotalSize + 1)));

    return {
      ...state,
      uploadedFileName,
      totalProgress,
    };
  };
